import {
  GetAlarmReceiversResponse,
  VoiceSmsSubscription,
} from '@features/sites/sitesAPI';
import { api } from '../../app/services/api';

type BooleanInt = 0 | 1;

export type PlivoRecordAlarm = {
  priority: number | null;
  alarm_date: string;
  alarm_text: string;
  controller: string;
  alarm_source: string;
  log_timestamp: string;
  controller_type: string;
  outdated: BooleanInt;
  filtered_in: BooleanInt;
  service_override: BooleanInt;
  outside_schedule_sms: BooleanInt;
  outside_schedule_voip: BooleanInt;
  outside_schedule_email: BooleanInt;
};

export type PlivoRecordStatus =
  | 'DELIVERED'
  | 'CONFIRMED'
  | 'SENT'
  | 'ANSWERED'
  | 'FAILED'
  | 'NOT SENT'
  | 'ERROR';

export type VoiceRecordStatus = 'SENT' | 'ANSWERED' | 'CONFIRMED' | 'FAILED';
export type SmsRecordStatus = 'SENT' | 'DELIVERED' | 'FAILED';

export type GetPlivoAlarmRecordsRequest = {
  store_num: number[];
  start_date: string;
  end_date: string;
};

export type PlivoAlarmRecord = {
  store_num: number;
  alarm: PlivoRecordAlarm;
  sms: {
    status: PlivoRecordStatus;
    sms_uuid: string;
    phone_number: string;
    log_timestamp: string;
    has_error: boolean;
    error_info: string | null;
  }[];
  voip: {
    status: PlivoRecordStatus;
    voip_uuid: string;
    phone_number: string;
    log_timestamp: string;
    has_error: boolean;
    error_info: string | null;
  }[];
};

export type GetPlivoAlarmRecordsResponse = PlivoAlarmRecord[];

export type GetPlivoSmsRecordsRequest = GetPlivoAlarmRecordsRequest;
export type GetPlivoVoiceRecordsRequest = GetPlivoAlarmRecordsRequest;

export type PlivoSmsRecord = {
  sms_uuid: string;
  log_timestamp: string;
  phone_number: string;
  phone_carrier: string;
  status: PlivoRecordStatus;
  store_num: number;
  alarms: Omit<PlivoRecordAlarm, 'outdated' | 'priority'>[];
  error_code: number;
  has_error: boolean;
  error_info: string | null;
};

export type CustomPlivoSmsRecord = Omit<PlivoSmsRecord, 'alarms'> & {
  custom_message: string;
};

export type PlivoVoiceRecord = {
  voip_uuid: string;
  log_timestamp: string;
  phone_number: string;
  phone_carrier: string;
  hangup_code: number | null;
  hangup_source: string | null;
  has_error: boolean;
  status: PlivoRecordStatus;
  store_num: number;
  alarms: Omit<PlivoRecordAlarm, 'outdated' | 'priority'>[];
};

export type VoiceCustomMessage = [
  { type: 'speak'; parameters: { content: string } },
  { type: 'wait'; parameters: { length: number } },
];

export type CustomPlivoVoiceRecord = Omit<PlivoVoiceRecord, 'alarms'> & {
  custom_message: VoiceCustomMessage;
};

export type GetPlivoSmsRecordsResponse = {
  leo_alert: PlivoSmsRecord[];
  custom: CustomPlivoSmsRecord[];
};

export type GetPlivoVoiceRecordsResponse = {
  leo_alert: PlivoVoiceRecord[];
  custom: CustomPlivoVoiceRecord[];
};

export type SendTestSMSRequest = {
  store_num: number;
  phone_number: string;
  phone_carrier: string;
};

export type ConfirmAlarmsRequest = {
  store_num: number;
  alarms: {
    controller_name: string;
    alarm_source: string;
    alarm_text: string;
    alarm_date: string;
  }[];
};
export type ConfirmAlarmsResponse = {
  sms_uuids: string[];
  voip_uuids: string[];
  alarm_date: string;
  alarm_text: string;
  controller: string;
  alarm_source: string;
}[];

export type RequestLeoAlertFilterRequest = {
  store_num: number;
  store_name: string;
  reason: string;
  email_address: string;
  additional_details: string;
  alarm_filters: {
    controller_name: string;
    alarm_source: string;
    alarm_text: string;
  }[];
};

export type StoresWithDateRange = {
  store_nums: Array<number>;
  start: string;
  end: string;
};

export type PlivoAlarmOverview = {
  alarm_count: number;
  service_override_count: number;
  filtered_in_count: number;
  outdated_count: number;
  alerted_count: number;
  filtered_overridden_count: number;
  filtered_outdated_count: number;
  filtered_outdated_overridden_count: number;
  store_num: number;
};

export type GetPlivoAlarmOverviewRequest = StoresWithDateRange;
export type GetPlivoAlarmOverviewResponse = PlivoAlarmOverview[];

export type PlivoCostReport = {
  ingested_alarm_count: number;
  alerted_alarm_count: number;
  sms_count: number;
  sms_cost: number;
  voip_count: number;
  voip_cost: number;
  total_cost: number;
  store_num: number;
};

export type GetPlivoCostReportRequest = StoresWithDateRange;
export type GetPlivoCostReportResponse = PlivoCostReport[];

export type GetLeoAlertScheduleRequest = { store_num: number };
export type GetLeoAlertScheduleResponse = {
  schedule_type: VoiceSmsSubscription;
  local_timezone: string | null;
  weekday_start_time: string | null;
  weekday_end_time: string | null;
  weekend_start_time: string | null;
  weekend_end_time: string | null;
};

export type SetLeoAlertScheduleRequest = GetLeoAlertScheduleRequest &
  GetLeoAlertScheduleResponse;

export type GetLeoAlertReceiversListForMultipleSitesRequest = {
  store_list: number[];
};
export type GetLeoAlertReceiversListForMultipleSitesResponse = {
  store_num: number;
  leo_alert_receivers: GetAlarmReceiversResponse;
}[];

export const plivoAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getPlivoAlarmRecords: builder.query<
      GetPlivoAlarmRecordsResponse,
      GetPlivoAlarmRecordsRequest
    >({
      query: (body) => ({
        url: `plivo/alarm-records`,
        method: 'POST',
        body,
      }),
    }),
    getSmsRecords: builder.query<
      (PlivoSmsRecord | CustomPlivoSmsRecord)[],
      GetPlivoSmsRecordsRequest
    >({
      query: (body) => ({
        url: `plivo/sms-records`,
        method: 'POST',
        body,
      }),
      transformResponse(baseQueryReturnValue: GetPlivoSmsRecordsResponse) {
        const newArr: (PlivoSmsRecord | CustomPlivoSmsRecord)[] = [
          ...baseQueryReturnValue.leo_alert,
          ...baseQueryReturnValue.custom,
        ];
        return newArr;
      },
    }),
    getVoiceRecords: builder.query<
      (PlivoVoiceRecord | CustomPlivoVoiceRecord)[],
      GetPlivoVoiceRecordsRequest
    >({
      query: (body) => ({
        url: `plivo/voice-records`,
        method: 'POST',
        body,
      }),
      transformResponse(baseQueryReturnValue: GetPlivoVoiceRecordsResponse) {
        const newArr: (PlivoVoiceRecord | CustomPlivoVoiceRecord)[] = [
          ...baseQueryReturnValue.leo_alert,
          ...baseQueryReturnValue.custom,
        ];
        return newArr;
      },
    }),
    sendTestSMS: builder.mutation<unknown, SendTestSMSRequest>({
      query: (body) => ({
        url: `plivo/send-test-sms`,
        method: 'POST',
        body,
      }),
    }),
    confirmAlarms: builder.query<ConfirmAlarmsResponse, ConfirmAlarmsRequest>({
      query: (body) => ({
        url: `plivo/confirm-alarms`,
        method: 'POST',
        body,
      }),
    }),
    requestLeoAlertFilter: builder.mutation<
      unknown,
      RequestLeoAlertFilterRequest
    >({
      query: (body) => ({
        url: `plivo/filter-request`,
        method: 'POST',
        body,
      }),
    }),
    getLeoAlertAlarmOverview: builder.query<
      GetPlivoAlarmOverviewResponse,
      GetPlivoAlarmOverviewRequest
    >({
      query: (body) => ({
        url: `plivo/alarm-overview`,
        method: 'POST',
        body,
      }),
    }),
    getLeoAlertCostReport: builder.query<
      GetPlivoCostReportResponse,
      GetPlivoCostReportRequest
    >({
      query: (body) => ({
        url: `plivo/cost-report`,
        method: 'POST',
        body,
        headers: {
          authorization: undefined,
        },
      }),
    }),
    getLeoAlertSchedule: builder.query<
      GetLeoAlertScheduleResponse,
      GetLeoAlertScheduleRequest
    >({
      query: (body) => ({
        url: 'plivo/schedule',
        method: 'POST',
        body,
      }),
      providesTags: ['LeoAlertSchedule'],
    }),
    setLeoAlertSchedule: builder.mutation<unknown, SetLeoAlertScheduleRequest>({
      query: (body) => ({
        url: 'plivo/schedule',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['LeoAlertSchedule'],
    }),
    getLeoAlertReceiversListForMultipleSites: builder.query<
      GetLeoAlertReceiversListForMultipleSitesResponse,
      GetLeoAlertReceiversListForMultipleSitesRequest
    >({
      query: (body) => ({
        url: 'plivo/alarm-receivers-list',
        method: 'POST',
        body,
      }),
      providesTags: ['AlarmReceivers'],
    }),
  }),
});

export const {
  useGetPlivoAlarmRecordsQuery,
  useLazyGetPlivoAlarmRecordsQuery,
  useGetSmsRecordsQuery,
  useLazyGetSmsRecordsQuery,
  useGetVoiceRecordsQuery,
  useLazyGetVoiceRecordsQuery,
  useSendTestSMSMutation,
  useConfirmAlarmsQuery,
  useRequestLeoAlertFilterMutation,
  useGetLeoAlertAlarmOverviewQuery,
  useGetLeoAlertCostReportQuery,
  useGetLeoAlertScheduleQuery,
  useSetLeoAlertScheduleMutation,

  useGetLeoAlertReceiversListForMultipleSitesQuery,
} = plivoAPI;
